import { getSupabase } from '@/lib/supabase'

/**
 * Machine-readable mutation outcomes (plan section 16). The server RPC returns one
 * of these so the client never has to guess whether a write landed.
 */
export type OperationOutcome =
  | 'applied'
  | 'replayed'
  | 'conflict'
  | 'invalid_input'
  | 'duplicate_imei'
  | 'already_sold'
  | 'unauthorized'

export interface ApplyOperationRequest {
  operation_id: string
  workspace_id: string
  entity_id: string
  expected_revision: number | null
  action: string
  payload: Record<string, unknown>
}

export interface FieldError {
  field: string
  message: string
}

export interface ApplyOperationResult {
  operation_id: string
  entity_id?: string
  status: OperationOutcome
  revision?: number
  server_sequence?: number
  retryable?: boolean
  field_errors?: FieldError[]
  /** Present on 'conflict': the authoritative state to compare against the local draft. */
  conflict?: { authoritative_revision: number; payload: Record<string, unknown> }
}

/**
 * A transport turns an outbox operation into a server result. Injected so the sync
 * engine is testable without Supabase, and so transport failure can be distinguished
 * from a definitive server rejection.
 */
export type SyncTransport = (req: ApplyOperationRequest) => Promise<ApplyOperationResult>

/**
 * Real transport: calls the `apply_operation` Postgres RPC. A thrown error means the
 * OUTCOME IS UNKNOWN (network/timeout), not that the mutation failed — the caller must
 * retry with the SAME operation_id, which the server replays idempotently.
 */
export function supabaseTransport(): SyncTransport {
  return async (req) => {
    const { data, error } = await getSupabase().rpc('apply_operation', {
      p_operation_id: req.operation_id,
      p_workspace_id: req.workspace_id,
      p_entity_id: req.entity_id,
      p_expected_revision: req.expected_revision,
      p_action: req.action,
      p_payload: req.payload,
    })
    if (error) throw error
    return data as ApplyOperationResult
  }
}
